import { useContext } from "react";
import "./SearchModal.scss";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { useLocation } from "react-router";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import {
	AsyncHotelOnlySearchFormContainer,
	AsyncSmartDPSearchFormContainer,
	AsyncStopoverSearchFormContainer,
} from "app/routes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import IconClose from "app/pages/.shared/IconClose";

const SearchForm = ({
	isStopover,
	isSDP,
	isHotelOnly,
	onSubmit,
	skipListingRedirect,
	skipLastSearchFallback,
	isLoading,
	submitButtonLabel,
}) => {
	if (isStopover) {
		return <AsyncStopoverSearchFormContainer onSubmit={onSubmit} />;
	}
	if (isSDP) {
		return (
			<AsyncSmartDPSearchFormContainer
				onSubmit={onSubmit}
				skipListingRedirect={skipListingRedirect}
				skipLastSearchFallback={skipLastSearchFallback}
				isLoading={isLoading}
				submitButtonLabel={submitButtonLabel}
			/>
		);
	}
	if (isHotelOnly) {
		return <AsyncHotelOnlySearchFormContainer onSubmit={onSubmit} />;
	}
	return null;
};

SearchForm.propTypes = {
	isStopover: PropTypes.bool,
	isSDP: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
	onSubmit: PropTypes.func,
	/** Only forwarded to the SDP form. */
	skipListingRedirect: PropTypes.bool,
	/** Only forwarded to the SDP form. */
	skipLastSearchFallback: PropTypes.bool,
	/** Only forwarded to the SDP form. */
	isLoading: PropTypes.bool,
	submitButtonLabel: PropTypes.node,
};

const SearchModal = ({
	title,
	isOpen,
	onClose,
	onSubmit,
	skipListingRedirect,
	skipLastSearchFallback,
	submitButtonLabel,
	isLoading,
	direction = "top",
}) => {
	const { pathname } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const isStopover = pathname.includes("stopover");
	const isSDP = pathname.includes("sdp");
	const isHotelOnly = pathname.includes("hotelonly");

	const overlayClassName = {
		top: "search-modal__overlay search-modal__overlay--top",
		bottom: "search-modal__overlay search-modal__overlay--bottom",
	};

	const contentClassName = {
		top: "search-modal__content search-modal__content--top",
		bottom: "search-modal__content search-modal__content--bottom",
	};

	return (
		<Modal
			isOpen={isOpen}
			closeTimeoutMS={200}
			onRequestClose={onClose}
			overlayClassName={overlayClassName[direction]}
			className={contentClassName[direction]}
		>
			{!isMobile && <HeaderContainer showMenuItems showDialPhone={false} showBackBtn />}

			{isMobile && (
				<section className="search-modal__title">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold component="h3">
						{title}
					</Typography>

					<IconClose
						width={24}
						height={24}
						onClick={onClose}
						className="search-modal__title-close-icon"
					/>
				</section>
			)}

			<SearchForm
				isSDP={isSDP}
				isStopover={isStopover}
				isHotelOnly={isHotelOnly}
				onSubmit={onSubmit ?? onClose}
				skipListingRedirect={skipListingRedirect}
				skipLastSearchFallback={skipLastSearchFallback}
				isLoading={isLoading}
				submitButtonLabel={submitButtonLabel}
			/>
		</Modal>
	);
};

SearchModal.propTypes = {
	title: PropTypes.string.isRequired,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	/** Defaults to closing the modal — pass a custom handler to do something else on submit (only reaches the SDP form). */
	onSubmit: PropTypes.func,
	/** Only forwarded to the SDP form. */
	skipListingRedirect: PropTypes.bool,
	/** Only forwarded to the SDP form. */
	skipLastSearchFallback: PropTypes.bool,
	/** Only forwarded to the SDP form. */
	isLoading: PropTypes.bool,
	submitButtonLabel: PropTypes.node,
	direction: PropTypes.oneOf(["top", "bottom"]),
};

export default SearchModal;
